import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  integrationsService,
  type CreateCapabilityDto,
  type ExecuteCapabilityDto,
  type ImportOpenAPIDto,
  type ImportYAMLDto,
  type UpdateCapabilityDto,
} from '@services/integrations.service';

// Query keys
export const integrationKeys = {
  all: ['integrations'] as const,
  catalog: (category?: string) => [...integrationKeys.all, 'catalog', category] as const,
  list: (filters?: { category?: string; status?: string; tags?: string }) =>
    [...integrationKeys.all, 'list', filters] as const,
  detail: (id: string) => [...integrationKeys.all, 'detail', id] as const,
  mcpSchema: (id: string) => [...integrationKeys.all, 'mcp-schema', id] as const,
  openaiSchema: (id: string) => [...integrationKeys.all, 'openai-schema', id] as const,
};

// Hooks
export function useCapabilityCatalog(category?: string, includeCustom = true) {
  return useQuery({
    queryKey: integrationKeys.catalog(category),
    queryFn: () => integrationsService.getCatalog(category, includeCustom),
  });
}

export function useCapabilities(filters?: {
  category?: string;
  status?: string;
  tags?: string;
}) {
  return useQuery({
    queryKey: integrationKeys.list(filters),
    queryFn: () => integrationsService.listCapabilities(filters),
  });
}

export function useCapability(capabilityId: string) {
  return useQuery({
    queryKey: integrationKeys.detail(capabilityId),
    queryFn: () => integrationsService.getCapability(capabilityId),
    enabled: !!capabilityId,
  });
}

export function useMcpSchema(capabilityId: string) {
  return useQuery({
    queryKey: integrationKeys.mcpSchema(capabilityId),
    queryFn: () => integrationsService.getMcpSchema(capabilityId),
    enabled: !!capabilityId,
  });
}

export function useOpenAISchema(capabilityId: string) {
  return useQuery({
    queryKey: integrationKeys.openaiSchema(capabilityId),
    queryFn: () => integrationsService.getOpenAISchema(capabilityId),
    enabled: !!capabilityId,
  });
}

export function useCreateCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCapabilityDto) => integrationsService.createCapability(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

export function useUpdateCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      capabilityId,
      data,
    }: {
      capabilityId: string;
      data: UpdateCapabilityDto;
    }) => integrationsService.updateCapability(capabilityId, data),
    onSuccess: (_, { capabilityId }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(capabilityId) });
    },
  });
}

export function useDeleteCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (capabilityId: string) => integrationsService.deleteCapability(capabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

export function useExecuteCapability() {
  return useMutation({
    mutationFn: ({
      capabilityId,
      data,
    }: {
      capabilityId: string;
      data: ExecuteCapabilityDto;
    }) => integrationsService.executeCapability(capabilityId, data),
  });
}

export function useTestCapability() {
  return useMutation({
    mutationFn: (capabilityId: string) => integrationsService.testCapability(capabilityId),
  });
}

export function useImportOpenAPI() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ImportOpenAPIDto) => integrationsService.importFromOpenAPI(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

export function useImportYAML() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ImportYAMLDto) => integrationsService.importFromYAML(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}

export function useImportFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      options,
    }: {
      file: File;
      options?: { connector_id?: string; prefix?: string };
    }) => integrationsService.importFromFile(file, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    },
  });
}
